import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { toast } from 'sonner';
import { formatPhoneForDisplay } from '@shared/phone/format-phone';
import { FINDING_LIVE_WINDOW_MS, usePhoneRequestStates } from './phone-requests-hooks';

/**
 * "Found +1 201 320 9233 for Mishti Sharma. Ready to dial." — the toast after a single-contact find (row button, contact
 * card, row menu). It auto-dismisses after 8 s, and a find that lands while it is still up joins it (same sonner id, so
 * the toast updates in place and sonner re-arms its timer from the time left: the full 8 s unless the rep had paused it
 * by hovering): close-together finds read as one toast, names for one or
 * two contacts and a count from three. A find after it closed opens a fresh one. The bulk path keeps its own "Refresh
 * dial selection" toast (`useEnrichOnDial`). There is no Dial button: Trellus only dials on the rep's real click on its
 * own button.
 *
 * Why a store and a shell-level watcher rather than the button's own hook: when the number arrives the list refetches
 * and the row's find button unmounts (rows without a number are the only ones that render it), so a toast raised from
 * the button would die with it. A click registers the contact here; `usePhoneFoundToasts` (mounted once at shell
 * level) watches only those contacts, so a result that was already on the page never toasts, and each request toasts
 * once (the entry is removed when it settles). The open batch also lives in the store, so a remounted or StrictMode
 * double-run watcher cannot lose or duplicate it.
 */
interface PendingFind {
  contactName: string | null;
  /** When the rep clicked; a result last updated before this belongs to an earlier search. */
  clickedAt: number;
  /**
   * The request the client already knew for this contact at click time, a finished earlier search; a result carrying
   * it is never this click's.
   */
  priorRequestId: string | null;
}

/** One found number, as the toast shows it. */
export interface FoundItem {
  contactName: string | null;
  value: string;
}

interface FoundBatch {
  id: string;
  /** Keyed by contact id so a re-find of the same contact overwrites rather than counts twice. */
  items: Record<string, FoundItem>;
  updatedAt: number;
}

interface PhoneFoundToastState {
  pending: Record<string, PendingFind>;
  /** The batch whose toast is (believed to be) on screen; null once closed. */
  batch: FoundBatch | null;
  batchSeq: number;
}

export const initialState: PhoneFoundToastState = { pending: {}, batch: null, batchSeq: 0 };

export const usePhoneFoundToastStore = create<PhoneFoundToastState>(() => ({ ...initialState }));

/** How long a found-number toast stays up; sonner owns the timer and re-arms it when the same id is updated. */
export const PHONE_FOUND_TOAST_MS = 8000;
/** Fallback if a close callback never arrives: a batch this old is treated as closed. */
const PHONE_FOUND_BATCH_STALE_MS = 2 * PHONE_FOUND_TOAST_MS;

/** Add finds to the open batch (same toast id, so sonner updates in place) or open a new one. */
export function recordFound(
  found: ReadonlyArray<{ contactId: string; contactName: string | null; value: string }>,
  now: number = Date.now(),
): FoundBatch {
  const s = usePhoneFoundToastStore.getState();
  const open = s.batch && now - s.batch.updatedAt <= PHONE_FOUND_BATCH_STALE_MS ? s.batch : null;
  const items: Record<string, FoundItem> = { ...open?.items };
  for (const f of found) items[f.contactId] = { contactName: f.contactName, value: f.value };
  const batchSeq = open ? s.batchSeq : s.batchSeq + 1;
  const next: FoundBatch = { id: open?.id ?? `phone-found-${batchSeq}`, items, updatedAt: now };
  usePhoneFoundToastStore.setState({ batch: next, batchSeq });
  return next;
}

/** The toast closed; id-guarded so a late callback from an older toast cannot close a newer batch. */
export function closeBatch(batchId: string): void {
  usePhoneFoundToastStore.setState((s) => (s.batch?.id === batchId ? { batch: null } : {}));
}

/** Watch `contactId` for a finished find; a later click for the same contact replaces the entry. */
export function watchPhoneFind(
  contactId: string,
  contactName: string | null,
  clickedAt: number,
  priorRequestId: string | null = null,
): void {
  usePhoneFoundToastStore.setState((s) => ({
    pending: { ...s.pending, [contactId]: { contactName, clickedAt, priorRequestId } },
  }));
}

function unwatchPhoneFinds(contactIds: readonly string[]): void {
  usePhoneFoundToastStore.setState((s) => {
    const pending = { ...s.pending };
    for (const id of contactIds) delete pending[id];
    return { pending };
  });
}

/**
 * Tolerates a little client/server clock skew when comparing a result's `updated_at` with the click. Both guards stay:
 * this one covers a finished result from long before the click, `priorRequestId` the previous request finished moments
 * before it (inside the skew).
 */
const CLOCK_SKEW_MS = 5000;
/** Past the request's own deadline (plus a grace for the reaper tick) a watch that never settled is dropped. */
export const WATCH_MAX_AGE_MS = FINDING_LIVE_WINDOW_MS;

/**
 * 1 found: the number (and the name, if known). 2 found, both named: both names. Otherwise an exact count.
 * Copy lives in this one pure function so a later wording change is one edit.
 */
export function foundPhoneToastText(items: readonly FoundItem[]): string {
  const [first, second] = items;
  if (first && items.length === 1) {
    const number = formatPhoneForDisplay(first.value);
    return first.contactName ? `Found ${number} for ${first.contactName}. Ready to dial.` : `Found ${number}. Ready to dial.`;
  }
  if (first?.contactName && second?.contactName && items.length === 2) {
    return `Found numbers for ${first.contactName} and ${second.contactName}. Ready to dial.`;
  }
  return `Found numbers for ${items.length} contacts. Ready to dial.`;
}

export function usePhoneFoundToasts(): void {
  const pending = usePhoneFoundToastStore((s) => s.pending);
  const ids = useMemo(() => Object.keys(pending).sort(), [pending]);
  const { byContactId } = usePhoneRequestStates(ids);

  useEffect(() => {
    const settled: string[] = [];
    const found: Array<{ contactId: string; contactName: string | null; value: string }> = [];
    // Read the store, not the render closure: a StrictMode second run must not re-find ids a first run settled.
    const live = usePhoneFoundToastStore.getState().pending;
    for (const id of ids) {
      const entry = live[id];
      if (!entry) continue;
      if (Date.now() - entry.clickedAt > WATCH_MAX_AGE_MS) {
        settled.push(id);
        continue;
      }
      const state = byContactId.get(id);
      if (!state) continue;
      if (state.state === 'finding') continue;
      // The earlier request this client already knew at click time is never this click's result.
      if (entry.priorRequestId !== null && state.request_id === entry.priorRequestId) continue;
      // A finished result from before this click is an earlier search's; keep waiting for this one.
      if (new Date(state.updated_at).getTime() < entry.clickedAt - CLOCK_SKEW_MS) continue;
      if (state.state === 'ready' && state.value) {
        found.push({ contactId: id, contactName: entry.contactName, value: state.value });
      }
      settled.push(id);
    }
    if (found.length > 0) {
      // ONE toast per pass. While the batch's toast is up the same id updates it in place (sonner re-arms its timer).
      const batch = recordFound(found);
      toast(foundPhoneToastText(Object.values(batch.items)), {
        id: batch.id,
        duration: PHONE_FOUND_TOAST_MS,
        dismissible: true,
        onAutoClose: () => closeBatch(batch.id),
        onDismiss: () => closeBatch(batch.id),
      });
    }
    if (settled.length > 0) unwatchPhoneFinds(settled);
  }, [ids, pending, byContactId]);

  // A watch that never gets a state (or whose results never change) still expires on its own; reads the store at fire time.
  useEffect(() => {
    const live = usePhoneFoundToastStore.getState().pending;
    const soonest = Math.min(...ids.map((id) => (live[id] ? live[id].clickedAt + WATCH_MAX_AGE_MS : Infinity)));
    if (!Number.isFinite(soonest)) return;
    const t = setTimeout(() => {
      const now = Date.now();
      const current = usePhoneFoundToastStore.getState().pending;
      unwatchPhoneFinds(ids.filter((id) => current[id] && now - current[id].clickedAt > WATCH_MAX_AGE_MS));
    }, Math.max(0, soonest - Date.now()) + 1);
    return () => clearTimeout(t);
  }, [ids]);
}
